import { Injectable } from '@angular/core';
import { TimeBlockModel } from '../../../../../../../core/models/timeblock/time-block.model';
import { DragPart } from '../drag-and-resize/drag-part';
import { DragData } from '../drag-and-resize/dragging/drag-view-controller/freely-day-or-week-drag-controller.service';
import { ResizeData } from '../drag-and-resize/resizing/resize-view-controller/vertical-day-or-week-resize-controller.service';

/**
 * This collision handler applies for both resizing and dragging of a time block.
 * We implement collision detection by ourselves and don't use the "restrict" property of interact.js,
 * since restriction doesn't work for scrolling.
 */
@Injectable()
export class CollisionHandlerService {
  public static collisionType: IntersectionType;
  private minBlockHeight = 0;
  private calendarBodyHeight: number;

  constructor() {
    CollisionHandlerService.collisionType = IntersectionType.IntersectNone;
  }

  public set CalendarBodyHeight(calendarBodyHeight: number) {
    this.calendarBodyHeight = calendarBodyHeight;
  }

  public set MinimumBlockHeight(minimumBlockHeight: number) {
    this.minBlockHeight = minimumBlockHeight;
  }

  /**
   * Calculate correct height for the block if it gets too small or hits a calendar edge, respectively, during resizing or dragging.
   */
  public checkVerticalIntersections(translationYData: ResizeData | DragData): void {
    if (this.minBlockHeight <= 0) {
      throw new Error('Minimum grid height must be greater than 0');
    }

    const posY = translationYData.dragEdgePos.y;
    CollisionHandlerService.collisionType = IntersectionType.IntersectNone;

    this.checkCalendarTopEdgeCollision(posY);
    this.checkCalendarBottomEdgeCollision(posY);
  }

  public checkDragEdgeSwitch(resizeData: ResizeData, timeBlockModel: TimeBlockModel): void {
    if (CollisionHandlerService.collisionType === IntersectionType.IntersectNone) {
      // The drag edge switch happens inside the calendar.
      this.checkSwitchInsideCalendar(resizeData, timeBlockModel);
    }
  }

  private checkCalendarTopEdgeCollision(posY: number): void {
    if (posY <= 0) {
      CollisionHandlerService.collisionType = IntersectionType.IntersectCalendarTopEdge;
    }
  }

  private checkCalendarBottomEdgeCollision(posY: number): void {
    if (posY >= this.calendarBodyHeight) {
      CollisionHandlerService.collisionType = IntersectionType.IntersectCalendarBottomEdge;
    }
  }

  private checkSwitchInsideCalendar(resizeData: ResizeData, timeBlockModel: TimeBlockModel): void {
    const geometryData = timeBlockModel.timeBlockViewType.geometryData;

    // If drag part is top and either the mouse cursor is south of the calendar bottom edge or a bottom edge collision has been detected.
    if (
      resizeData.dragPart === DragPart.Top &&
      resizeData.mousePos.y > geometryData.calendarTopEdgeToTimeBlockBottomEdge
    ) {
      CollisionHandlerService.collisionType = IntersectionType.IntersectWithDragBottomEdge;
    } else if (
      // If drag part is bottom and either the mouse cursor is north of the calendar top edge or a top edge collision has been detected.
      resizeData.dragPart === DragPart.Bottom &&
      resizeData.mousePos.y < timeBlockModel.timeBlockViewType.geometryData.top
    ) {
      CollisionHandlerService.collisionType = IntersectionType.IntersectWithDragTopEdge;
    }
  }
}

export enum IntersectionType {
  IntersectCalendarTopEdge,
  IntersectCalendarBottomEdge,
  IntersectCalendarLeftEdge,
  IntersectCalendarRightEdge,
  IntersectWithDragTopEdge,
  IntersectWithDragBottomEdge,
  IntersectNone,
}
